import { useEffect, useState, lazy, Suspense } from 'react';
import { HashRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Nav } from './ui/Nav';
import { seedIfNeeded } from './seed';
import { syncNow } from './sync/syncClient';
import { hydrateSyncConfig } from './sync/config';
import { TodayScreen } from './features/today/TodayScreen';
import { SessionScreen } from './features/session/SessionScreen';
import { AnalysisScreen } from './features/analysis/AnalysisScreen';
import { CoachScreen } from './features/coach/CoachScreen';
import { MoreScreen } from './features/more/MoreScreen';
import { SettingsScreen } from './features/settings/SettingsScreen';
import { VolumeScreen } from './features/volume/VolumeScreen';

// Chart-heavy screens (Recharts) are lazy so the gym capture path stays lean.
const ExerciseTrendScreen = lazy(() => import('./features/history/ExerciseTrendScreen').then((m) => ({ default: m.ExerciseTrendScreen })));
const MeasurementsScreen = lazy(() => import('./features/measurements/MeasurementsScreen').then((m) => ({ default: m.MeasurementsScreen })));

function Shell() {
  const location = useLocation();
  const hideNav = location.pathname.startsWith('/session/');
  return (
    <div className="app">
      <Suspense fallback={<div className="empty">Loading...</div>}>
        <Routes>
          <Route path="/" element={<TodayScreen />} />
          <Route path="/session/:id" element={<SessionScreen />} />
          <Route path="/analysis" element={<AnalysisScreen />} />
          <Route path="/analysis/:exerciseId" element={<ExerciseTrendScreen />} />
          <Route path="/coach" element={<CoachScreen />} />
          <Route path="/more" element={<MoreScreen />} />
          <Route path="/more/settings" element={<SettingsScreen />} />
          <Route path="/more/volume" element={<VolumeScreen />} />
          <Route path="/more/measurements" element={<MeasurementsScreen />} />
        </Routes>
      </Suspense>
      {!hideNav && <Nav />}
    </div>
  );
}

export function App() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await seedIfNeeded();
      // Hydrate sync config from IndexedDB (with one-time localStorage migration)
      // BEFORE the first syncNow, or isSyncConfigured() reads an empty cache.
      await hydrateSyncConfig();
      // Ask the browser not to evict our storage. This is what stops iOS from
      // silently clearing credentials + queued sessions under storage pressure.
      if (typeof navigator !== 'undefined' && navigator.storage?.persist) {
        void navigator.storage.persist();
      }
      if (!cancelled) setReady(true);
      // Background sync attempt; safe no-op when Notion is not configured.
      void syncNow();
    })();

    const onOnline = () => void syncNow();
    // visibilitychange is far more reliable than 'online' on mobile PWAs: it fires
    // every time the app returns to the foreground, which is when a finished gym
    // session typically gets a chance to flush.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void syncNow();
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  if (!ready) {
    return (
      <div className="app">
        <div className="empty">Loading...</div>
      </div>
    );
  }

  return (
    <HashRouter>
      <Shell />
    </HashRouter>
  );
}
